"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Leaf, ArrowRight, CircleCheck } from "lucide-react";
function InquiryFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="147716" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 bg-primary/10 rounded-[4px] flex items-center justify-center mb-4">
                    <CircleCheck className="size-8 text-primary" />
                </div>
                <h3 className="text-2xl font-sans font-bold text-foreground">Inquiry Received</h3>
                <p className="text-muted-foreground max-w-sm">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-6 rounded-[4px] px-8 border-primary text-primary hover:bg-primary/5">
                    Submit New Inquiry
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="Jane Doe" className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary h-12 rounded-[4px] transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">Enter the name of the person we should contact.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="jane@example.com" className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary h-12 rounded-[4px] transition-all duration-300" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Phone Number</FormLabel>
                            <FormControl>
                                <Input type="tel" placeholder="+1 (555) 123-14567" className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary h-12 rounded-[4px] transition-all duration-300" {...field} />
                            </FormControl>
                            <FormDescription className="text-[10px]">Include country code for international callers.</FormDescription>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="organization" rules={{
        required: "Organization / Agency is required"
      }} render={({
        field
      }) => <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Organization / Agency</FormLabel>
                            <FormControl>
                                <Input placeholder="Department of Finance" className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary h-12 rounded-[4px] transition-all duration-300" {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
                        <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground font-bold">Message</FormLabel>
                        <FormControl>
                            <Textarea placeholder="Briefly describe your ERP needs or questions..." className="bg-background border-border focus-visible:ring-primary focus-visible:border-primary min-h-[120px] rounded-[4px] resize-none transition-all duration-300" {...field} />
                        </FormControl>
                        <FormDescription className="text-[10px]">Optional – providing details helps us prepare for the call.</FormDescription>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="source" render={({
      field
    }) => <input type="hidden" {...field} />} />

            {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-[4px] text-destructive text-sm flex items-start gap-3">
                    <p>{submitError}</p>
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-bold rounded-[4px] bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md transition-all duration-300 group">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Processing...
                    </> : <>
                        Send Message
                        <ArrowRight className="ml-2 size-5 group-hover:translate-x-1 transition-transform" />
                    </>}
            </Button>
        </Form>;
}
export default function Inquiry() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  return <section id="contact" className="relative py-24 lg:py-32 bg-background text-foreground overflow-hidden" ref={sectionRef}>
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[70%] rounded-full bg-primary/5 blur-3xl opacity-30" />
                <div className="absolute -bottom-[20%] -left-[10%] w-[60%] h-[60%] rounded-full bg-secondary/5 blur-3xl opacity-30" />
            </div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
                    
                    <div className={`lg:col-span-5 flex flex-col justify-center transition-all duration-1000 ease-out ${isSectionVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-bold w-fit mb-6">
                            <Leaf className="size-4" />
                            <span>Digital Institutionalism</span>
                        </div>
                        
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-sans font-bold text-foreground leading-tight tracking-tight mb-6">
                            Connect with <br className="hidden lg:block" />
                            <span className="text-primary">Our Specialists</span> <br className="hidden lg:block" />
                            Today.
                        </h2>
                        
                        <p className="text-lg text-muted-foreground mb-8 max-w-md leading-relaxed">
                            Connect with GovERP's expert implementation team. Share your departmental requirements, and we'll provide a comprehensive, secure ERP strategy tailored to your agency's unique needs.
                        </p>

                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 text-sm font-bold text-foreground">
                            <div className="flex items-center gap-2">
                                <div className="size-10 rounded-[4px] bg-secondary/10 flex items-center justify-center text-secondary">
                                    <CircleCheck className="size-5" />
                                </div>
                                <span>FISMA Compliant</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="size-10 rounded-[4px] bg-secondary/10 flex items-center justify-center text-secondary">
                                    <CircleCheck className="size-5" />
                                </div>
                                <span>SOC2 Certified</span>
                            </div>
                        </div>
                    </div>

                    <div ref={formRef} className={`lg:col-span-7 lg:pl-12 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                        <Card className="bg-card border-border shadow-lg rounded-[4px] overflow-hidden relative">
                            <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
                            
                            <CardHeader className="pb-6 pt-8 px-8">
                                <CardTitle className="text-2xl font-sans font-bold">Contact Us</CardTitle>
                                <CardDescription className="text-base">
                                    Complete the form below to connect with a government systems specialist and explore our modular ERP solutions.
                                </CardDescription>
                            </CardHeader>
                            
                            <CardContent className="px-8 pb-8">
                                <FormProvider formKey="contact_us_form_home" formId="contact_us_form_home" sectionName="Inquiry" defaultValues={{
                full_name: "",
                email: "",
                phone: "",
                organization: "",
                message: "",
                source: "home_page"
              }} validationRules={{
                full_name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                phone: {
                  required: true,
                  pattern: "phone"
                },
                organization: {
                  required: true
                }
              }} submitText="Send Message" successMessage="Thank you! We have received your inquiry and a systems specialist will contact you shortly to schedule your consultation.">
                                    <InquiryFormInner />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}